'use client';
import React from 'react';
import AppImage from '@/components/ui/AppImage';
import { Quotation } from '@/lib/types';
import { formatINR, amountInWords } from '@/lib/utils';
import { VALID_GST_RATES } from '@/lib/catalogue';

interface QuotationPreviewProps {
  quotation: Quotation;
  subtotal: number;
  totalGST: number;
  grandTotal: number;
  cgst: number;
  sgst: number;
}

const PRINT_FONT = "'Arial', 'Helvetica Neue', Helvetica, sans-serif";
const MONO_FONT = "'Courier New', Courier, monospace";

const cell: React.CSSProperties = {
  fontFamily: PRINT_FONT,
  fontSize: '11px',
  lineHeight: '1.4',
  padding: '6px 8px',
  verticalAlign: 'middle',
  borderBottom: '1px solid #e5e7eb',
};

const numCell: React.CSSProperties = {
  ...cell,
  fontFamily: MONO_FONT,
  textAlign: 'right',
  whiteSpace: 'nowrap',
  fontVariantNumeric: 'tabular-nums',
};

const thCell: React.CSSProperties = {
  fontFamily: PRINT_FONT,
  fontSize: '11px',
  fontWeight: 700,
  padding: '8px',
  color: '#ffffff',
  whiteSpace: 'nowrap',
  verticalAlign: 'middle',
};

export default function QuotationPreview({
  quotation,
  subtotal,
  totalGST,
  grandTotal,
  cgst,
  sgst,
}: QuotationPreviewProps) {
  const q = quotation;

  return (
    <div
      id="quotation-print-area"
      style={{
        maxWidth: '900px',
        margin: '0 auto',
        backgroundColor: '#ffffff',
        fontFamily: PRINT_FONT,
        boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
        borderRadius: '16px',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div style={{ backgroundColor: '#6aaa64', padding: '24px 32px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ width: '64px', height: '64px', borderRadius: '12px', backgroundColor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
              <AppImage
                src="/assets/images/Thanks-Giving-Dinner-32-1777612945004.png"
                alt="Terrace Garden India logo"
                width={56}
                height={56}
                className="object-contain"
              />
            </div>
            <div>
              <h1 style={{ fontFamily: PRINT_FONT, fontSize: '22px', fontWeight: 700, color: '#ffffff', margin: 0, lineHeight: '1.2' }}>Terrace Garden India</h1>
              <p style={{ fontFamily: PRINT_FONT, fontSize: '12px', color: 'rgba(255,255,255,0.8)', margin: '4px 0 0' }}>Transforming Urban Spaces into Vibrant Green Havens</p>
              <p style={{ fontFamily: PRINT_FONT, fontSize: '11px', color: 'rgba(255,255,255,0.7)', margin: '2px 0 0' }}>ISO 9001:2015 Certified · Est. 1995 · 250+ Projects</p>
            </div>
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div style={{ backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '12px', padding: '12px 20px' }}>
              <p style={{ fontFamily: PRINT_FONT, fontSize: '10px', color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 4px' }}>Quotation</p>
              <p style={{ fontFamily: MONO_FONT, fontSize: '16px', fontWeight: 700, color: '#ffffff', margin: 0 }}>{q.quoteNumber}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Company Address Bar */}
      <div style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb', padding: '10px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <span style={{ fontFamily: PRINT_FONT, fontSize: '11px', color: '#6b7280' }}>26/5, H1 Block, Qutub Vihar Ph-1, Dwarka Sector-19, New Delhi – 110071</span>
          <span style={{ color: '#d1d5db' }}>|</span>
          <span style={{ fontFamily: PRINT_FONT, fontSize: '11px', color: '#6b7280' }}>+91 96259 52842 · +91 93127 09451</span>
        </div>
        <span style={{ fontFamily: PRINT_FONT, fontSize: '11px', color: '#9ca3af' }}>www.terracegardenindia.com</span>
      </div>

      <div style={{ padding: '24px 32px' }}>
        {/* Quote Meta + Client */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
          {/* Quote Details */}
          <div>
            <h3 style={{ fontFamily: PRINT_FONT, fontSize: '10px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 12px' }}>Quotation Details</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {[
                { label: 'Date:', value: q.date },
                { label: 'Valid Until:', value: q.validUntil },
                { label: 'Prepared By:', value: q.preparedBy },
              ].map(({ label, value }) => (
                <div key={label} style={{ display: 'flex', gap: '12px' }}>
                  <span style={{ fontFamily: PRINT_FONT, fontSize: '11px', color: '#9ca3af', width: '90px', flexShrink: 0 }}>{label}</span>
                  <span style={{ fontFamily: PRINT_FONT, fontSize: '11px', fontWeight: 600, color: '#1f2937' }}>{value}</span>
                </div>
              ))}
              <div style={{ display: 'flex', gap: '12px' }}>
                <span style={{ fontFamily: PRINT_FONT, fontSize: '11px', color: '#9ca3af', width: '90px', flexShrink: 0 }}>GST Type:</span>
                <span style={{ fontFamily: PRINT_FONT, fontSize: '11px', fontWeight: 600, color: '#6aaa64' }}>{q.gstType}</span>
              </div>
            </div>
          </div>

          {/* Client Details */}
          <div>
            <h3 style={{ fontFamily: PRINT_FONT, fontSize: '10px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 12px' }}>Bill To</h3>
            <div style={{ backgroundColor: '#f9fafb', borderRadius: '12px', padding: '12px', border: '1px solid #e5e7eb' }}>
              <p style={{ fontFamily: PRINT_FONT, fontSize: '13px', fontWeight: 700, color: '#111827', margin: '0 0 2px' }}>{q.clientName}</p>
              {q.clientCompany && <p style={{ fontFamily: PRINT_FONT, fontSize: '11px', color: '#6b7280', margin: '0 0 2px' }}>{q.clientCompany}</p>}
              {q.clientAddress && <p style={{ fontFamily: PRINT_FONT, fontSize: '11px', color: '#6b7280', margin: '4px 0 0' }}>{q.clientAddress}</p>}
              <p style={{ fontFamily: PRINT_FONT, fontSize: '11px', color: '#6b7280', margin: '2px 0 0' }}>
                {[q.clientCity, q.clientState, q.clientPIN].filter(Boolean).join(', ')}
              </p>
              {q.clientPhone && <p style={{ fontFamily: PRINT_FONT, fontSize: '11px', color: '#6b7280', margin: '4px 0 0' }}>{q.clientPhone}</p>}
              {q.clientEmail && <p style={{ fontFamily: PRINT_FONT, fontSize: '11px', color: '#6b7280', margin: '2px 0 0' }}>{q.clientEmail}</p>}
              {q.clientGSTIN && (
                <p style={{ fontFamily: MONO_FONT, fontSize: '11px', color: '#374151', margin: '6px 0 0' }}>
                  GSTIN: <strong>{q.clientGSTIN}</strong>
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Subject */}
        {q.subject && (
          <div style={{ marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid #e5e7eb' }}>
            <p style={{ fontFamily: PRINT_FONT, fontSize: '10px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, margin: '0 0 4px' }}>Subject</p>
            <p style={{ fontFamily: PRINT_FONT, fontSize: '13px', fontWeight: 600, color: '#1f2937', margin: 0 }}>{q.subject}</p>
          </div>
        )}

        {/* Line Items Table */}
        <div style={{ marginBottom: '24px', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden', tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '36px' }} />
              <col />
              <col style={{ width: '72px' }} />
              <col style={{ width: '48px' }} />
              <col style={{ width: '48px' }} />
              <col style={{ width: '80px' }} />
              <col style={{ width: '52px' }} />
              <col style={{ width: '88px' }} />
              <col style={{ width: '88px' }} />
              <col style={{ width: '88px' }} />
            </colgroup>
            <thead>
              <tr style={{ backgroundColor: '#6aaa64' }}>
                <th style={{ ...thCell, textAlign: 'center' }}>Sr.</th>
                <th style={{ ...thCell, textAlign: 'left' }}>Description</th>
                <th style={{ ...thCell, textAlign: 'left' }}>HSN/SAC</th>
                <th style={{ ...thCell, textAlign: 'center' }}>Unit</th>
                <th style={{ ...thCell, textAlign: 'center' }}>Qty</th>
                <th style={{ ...thCell, textAlign: 'right' }}>Rate (₹)</th>
                <th style={{ ...thCell, textAlign: 'center' }}>GST%</th>
                <th style={{ ...thCell, textAlign: 'right' }}>Amount (₹)</th>
                <th style={{ ...thCell, textAlign: 'right' }}>GST Amt (₹)</th>
                <th style={{ ...thCell, textAlign: 'right' }}>Total (₹)</th>
              </tr>
            </thead>
            <tbody>
              {q.lineItems.map((li, idx) => (
                <tr key={li.id} style={{ backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f9fafb' }}>
                  <td style={{ ...cell, textAlign: 'center', color: '#9ca3af' }}>{li.srNo}</td>
                  <td style={{ ...cell, color: '#1f2937', fontWeight: 500, wordBreak: 'break-word' }}>{li.description}</td>
                  <td style={{ ...cell, fontFamily: MONO_FONT, color: '#6b7280', fontSize: '10px' }}>{li.hsn}</td>
                  <td style={{ ...cell, textAlign: 'center', color: '#6b7280' }}>{li.unit}</td>
                  <td style={{ ...cell, textAlign: 'center', color: '#1f2937', fontWeight: 600 }}>{li.qty}</td>
                  <td style={{ ...numCell }}>{li.rate.toLocaleString('en-IN')}</td>
                  <td style={{ ...cell, textAlign: 'center', color: '#6b7280' }}>{li.gstRate}%</td>
                  <td style={{ ...numCell, fontWeight: 500 }}>{li.amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                  <td style={{ ...numCell, color: '#6b7280' }}>{li.gstAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                  <td style={{ ...numCell, fontWeight: 700, color: '#6aaa64' }}>{li.total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '24px' }}>
          <div style={{ width: '288px' }}>
            <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden' }}>
              <div style={{ padding: '10px 16px', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e5e7eb' }}>
                <span style={{ fontFamily: PRINT_FONT, fontSize: '11px', color: '#9ca3af' }}>Subtotal (ex-GST)</span>
                <span style={{ fontFamily: MONO_FONT, fontSize: '11px', fontWeight: 600, color: '#1f2937', whiteSpace: 'nowrap' }}>{formatINR(subtotal)}</span>
              </div>
              {q.gstType === 'CGST+SGST' ? (
                <>
                  <div style={{ padding: '8px 16px', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f3f4f6' }}>
                    <span style={{ fontFamily: PRINT_FONT, fontSize: '11px', color: '#9ca3af' }}>CGST (9%)</span>
                    <span style={{ fontFamily: MONO_FONT, fontSize: '11px', color: '#374151', whiteSpace: 'nowrap' }}>{formatINR(cgst)}</span>
                  </div>
                  <div style={{ padding: '8px 16px', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e5e7eb' }}>
                    <span style={{ fontFamily: PRINT_FONT, fontSize: '11px', color: '#9ca3af' }}>SGST (9%)</span>
                    <span style={{ fontFamily: MONO_FONT, fontSize: '11px', color: '#374151', whiteSpace: 'nowrap' }}>{formatINR(sgst)}</span>
                  </div>
                </>
              ) : (
                <div style={{ padding: '8px 16px', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e5e7eb' }}>
                  <span style={{ fontFamily: PRINT_FONT, fontSize: '11px', color: '#9ca3af' }}>IGST (18%)</span>
                  <span style={{ fontFamily: MONO_FONT, fontSize: '11px', color: '#374151', whiteSpace: 'nowrap' }}>{formatINR(totalGST)}</span>
                </div>
              )}
              <div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', backgroundColor: '#6aaa64' }}>
                <span style={{ fontFamily: PRINT_FONT, fontSize: '13px', fontWeight: 700, color: '#ffffff' }}>Grand Total</span>
                <span style={{ fontFamily: MONO_FONT, fontSize: '13px', fontWeight: 700, color: '#ffffff', whiteSpace: 'nowrap' }}>{formatINR(grandTotal)}</span>
              </div>
            </div>
            {grandTotal > 0 && (
              <div style={{ marginTop: '12px', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '12px 16px' }}>
                <p style={{ fontFamily: PRINT_FONT, fontSize: '10px', color: '#9ca3af', margin: '0 0 4px' }}>Amount in Words</p>
                <p style={{ fontFamily: PRINT_FONT, fontSize: '11px', color: '#374151', fontWeight: 500, fontStyle: 'italic', lineHeight: '1.5', margin: 0 }}>{amountInWords(grandTotal)}</p>
              </div>
            )}
          </div>
        </div>

        {/* GST Summary Table */}
        {q.lineItems.some((li) => li.gstAmount > 0) && (
          <div style={{ marginBottom: '24px' }}>
            <p style={{ fontFamily: PRINT_FONT, fontSize: '10px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 8px' }}>GST Summary</p>
            <table style={{ borderCollapse: 'collapse', border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden', minWidth: '360px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f3f4f6' }}>
                  <th style={{ ...thCell, color: '#6b7280', textAlign: 'left' }}>GST Rate</th>
                  <th style={{ ...thCell, color: '#6b7280', textAlign: 'right' }}>Taxable Amount</th>
                  {q.gstType === 'CGST+SGST' ? (
                    <>
                      <th style={{ ...thCell, color: '#6b7280', textAlign: 'right' }}>CGST</th>
                      <th style={{ ...thCell, color: '#6b7280', textAlign: 'right' }}>SGST</th>
                    </>
                  ) : (
                    <th style={{ ...thCell, color: '#6b7280', textAlign: 'right' }}>IGST</th>
                  )}
                  <th style={{ ...thCell, color: '#6b7280', textAlign: 'right' }}>Total GST</th>
                </tr>
              </thead>
              <tbody>
                {VALID_GST_RATES.filter((r) => q.lineItems.some((li) => li.gstRate === r && li.gstAmount > 0)).map((rate, i) => {
                  const taxable = q.lineItems.filter((li) => li.gstRate === rate).reduce((s, li) => s + li.amount, 0);
                  const gstAmt = q.lineItems.filter((li) => li.gstRate === rate).reduce((s, li) => s + li.gstAmount, 0);
                  return (
                    <tr key={`gst-sum-${rate}`} style={{ backgroundColor: i % 2 === 0 ? '#ffffff' : '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ ...cell, fontWeight: 500, color: '#374151' }}>{rate}%</td>
                      <td style={{ ...numCell, color: '#374151' }}>{formatINR(taxable)}</td>
                      {q.gstType === 'CGST+SGST' ? (
                        <>
                          <td style={{ ...numCell, color: '#374151' }}>{formatINR(gstAmt / 2)}</td>
                          <td style={{ ...numCell, color: '#374151' }}>{formatINR(gstAmt / 2)}</td>
                        </>
                      ) : (
                        <td style={{ ...numCell, color: '#374151' }}>{formatINR(gstAmt)}</td>
                      )}
                      <td style={{ ...numCell, fontWeight: 600, color: '#1f2937' }}>{formatINR(gstAmt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Terms & Notes */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
          <div>
            <p style={{ fontFamily: PRINT_FONT, fontSize: '10px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 8px' }}>Terms & Conditions</p>
            <p style={{ fontFamily: PRINT_FONT, fontSize: '11px', color: '#6b7280', lineHeight: '1.6', margin: 0 }}>{q.terms}</p>
          </div>
          <div>
            <p style={{ fontFamily: PRINT_FONT, fontSize: '10px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 8px' }}>Notes</p>
            <p style={{ fontFamily: PRINT_FONT, fontSize: '11px', color: '#6b7280', lineHeight: '1.6', margin: 0 }}>{q.notes}</p>
          </div>
        </div>

        {/* Signature Block */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #e5e7eb' }}>
          <div>
            <p style={{ fontFamily: PRINT_FONT, fontSize: '11px', color: '#9ca3af', margin: '0 0 32px' }}>Client Signature & Acceptance</p>
            <div style={{ borderBottom: '1px solid #9ca3af', marginBottom: '4px' }}></div>
            <p style={{ fontFamily: PRINT_FONT, fontSize: '11px', color: '#9ca3af', margin: 0 }}>Name / Date</p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{ fontFamily: PRINT_FONT, fontSize: '11px', color: '#9ca3af', margin: '0 0 8px' }}>For Terrace Garden India</p>
            <div style={{ borderBottom: '1px solid #9ca3af', marginBottom: '4px', marginTop: '24px' }}></div>
            <p style={{ fontFamily: PRINT_FONT, fontSize: '12px', fontWeight: 600, color: '#374151', margin: '0 0 2px' }}>{q.preparedBy}</p>
            <p style={{ fontFamily: PRINT_FONT, fontSize: '11px', color: '#9ca3af', margin: 0 }}>Authorised Signatory</p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{ backgroundColor: '#6aaa64', padding: '16px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
        <span style={{ fontFamily: PRINT_FONT, fontSize: '11px', color: 'rgba(255,255,255,0.8)' }}>© Terrace Garden India · www.terracegardenindia.com · ISO 9001:2015 Certified</span>
        <span style={{ fontFamily: PRINT_FONT, fontSize: '11px', color: 'rgba(255,255,255,0.8)' }}>+91 96259 52842 | +91 93127 09451 | indiaterracegarden@gmail.com</span>
      </div>
    </div>
  );
}